import React, { useMemo } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  Linking,
  ActivityIndicator,
} from 'react-native';
import lightStyles, { createHomeScreenThemeStyles } from '../HomeScreen.style';
import { TEXT } from '../../../constants/texts';
import { useSelector } from 'react-redux';
import LinkIcon from './icons/LinkIcon';
import { useTheme } from '../../../theme';

interface ExternalLinkItem {
  id: string | number;
  linkTitle?: string;
  linkUrl?: string;
  title?: string;
  messageText?: string;
  category?: string;
  order?: number;
  displayOrder?: number;
  link?: {
    Description?: string;
    Url?: string;
  };
  image: {
    Description: string;
    Url: string;
  };
  createdDate: string;
  modifiedDate: string;
}

interface ExternalLinksProps {
  externalLinks: ExternalLinkItem[] | string[];
  setExternalLinksOverlayVisible: (isVisible: boolean) => void;
}

const ExternalLinks: React.FC<ExternalLinksProps> = ({
  externalLinks,
  setExternalLinksOverlayVisible,
}) => {
  const { colors } = useTheme();
  const themeStyles = useMemo(() => createHomeScreenThemeStyles(colors), [colors]);
  // Get loading and error states from Redux
  const { loading } = useSelector((state: any) => state.homescreen);
  const isExternalLinksLoading = loading.externalLinks;
  // Helper function to handle URL opening with error handling
  const handleLinkPress = (url: string, description: string) => {
    if (url) {
      Linking.openURL(url).catch((err) => {
        console.error("An error occurred opening the link:", err);
      });
    } else {
      console.log(`No URL available for: ${description}`);
    }
  };

  // Function to check if item is new API format or legacy string format
  const isNewFormat = (item: ExternalLinkItem | string): item is ExternalLinkItem => {
    return typeof item === "object" && item !== null;
  };

  const renderExternalLink = (link: ExternalLinkItem | string, index: number) => {
    if (isNewFormat(link)) {
      const url = link.linkUrl || link.link?.Url || "";
      const displayPrimary = link.linkTitle || link.link?.Description || link.title || "";
      const hasLink = Boolean(url);

      return (
        <View key={link.id || index}>
          <TouchableOpacity
            activeOpacity={0.8}
            style={lightStyles.linkRow}
            onPress={() => {
              if (hasLink) {
                handleLinkPress(url, displayPrimary);
              } else {
                console.log(`Link clicked: ${displayPrimary}`);
              }
            }}
          >
            <View style={[lightStyles.linkIconContainer, themeStyles.linkIconContainer]}>
              <LinkIcon size={16} color={colors.brand.primary} />
            </View>
            <View style={lightStyles.linkTextContainer}>
              <Text style={[lightStyles.linkTextPrimary, themeStyles.linkTextPrimary]} numberOfLines={2}>
                {displayPrimary}
              </Text>
            </View>
          </TouchableOpacity>
          {/* Divider matching Figma */}
          <View style={[lightStyles.linkDivider, themeStyles.linkDivider]} />
        </View>
      );
    }

    // Legacy string format
    return (
      <View key={index}>
        <TouchableOpacity
          activeOpacity={0.8}
          style={lightStyles.linkRow}
          onPress={() => console.log(`Link clicked: ${link}`)}
        >
          <View style={[lightStyles.linkIconContainer, themeStyles.linkIconContainer]}>
            <LinkIcon size={16} color={colors.brand.primary} />
          </View>
          <View style={lightStyles.linkTextContainer}>
            <Text style={[lightStyles.linkTextPrimary, themeStyles.linkTextPrimary]} numberOfLines={2}>
              {link}
            </Text>
          </View>
        </TouchableOpacity>
        <View style={[lightStyles.linkDivider, themeStyles.linkDivider]} />
      </View>
    );
  };

  // Check if we have data to display
  const hasData = externalLinks && externalLinks.length > 0;

  // Show loading state
  if (isExternalLinksLoading) {
    return (
      <View style={[lightStyles.bottomCard, themeStyles.cardSurface]}>
        <Text style={[lightStyles.sectionHeader, themeStyles.sectionHeader]}>{TEXT.HEADERS.EXTERNAL_LINKS}</Text>
        <View style={lightStyles.externalLoadingContainer}>
          <ActivityIndicator size="large" color={colors.brand.primary} />
          <Text style={[lightStyles.externalLoadingText, themeStyles.externalLoadingText]}>Loading external links...</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={[lightStyles.bottomCard, themeStyles.cardSurface]}>
      <Text style={[lightStyles.sectionHeader, themeStyles.sectionHeader]}>{TEXT.HEADERS.EXTERNAL_LINKS}</Text>
      <ScrollView style={lightStyles.scrollContent}>
        {hasData ? (
          <>
            {externalLinks.map((link: any, index: number) => (
              <React.Fragment key={typeof link === 'object' ? (link.id || index) : `external-link-${index}`}>
                {renderExternalLink(link, index)}
              </React.Fragment>
            ))}
          </>
        ) : (
          <View style={lightStyles.noDataContainer}>
            <Text style={[lightStyles.noDataText, themeStyles.noDataText]}>{TEXT.COMMON.NO_DATA}</Text>
          </View>
        )}
      </ScrollView>
    </View>
  );
};

export default ExternalLinks;
